// rtlsMap.js — RTLS facility map with Framer Motion expand animation + resize sync
//
// Animation strategy
// ─────────────────
// CSS cannot transition `position: static ↔ fixed`, so pure CSS always snaps.
// Instead we:
//   1. Render a lightweight <div ref={spacerRef}> in the grid that reserves the
//      column space.  It is always invisible — it only exists for measurement.
//   2. Render the real card in a React Portal attached to document.body.
//      The card is ALWAYS `position: fixed`; we simply animate its left/top/
//      width/height/borderRadius using Framer Motion useMotionValue.
//   3. A ResizeObserver on the spacer keeps the card in sync with the spacer's
//      position every frame — including while the sidebar width-transition is
//      running — with zero spring lag (direct `.set()` calls).
//   4. Expand / collapse uses `animate()` from Framer Motion with a spring so
//      the card sweeps smoothly to/from full-screen.
//
// Resize notification strategy
// ───────────────────────────
// The Riot.Map SDK source (Nl class) has no static resize() method.
// However two mechanisms keep the iframe correctly redrawn at every size:
//
//   A. Browser-native resize events:
//      The iframe is `position: absolute; inset: 0` inside a `position: relative`
//      container. Framer Motion changes the container's width/height inline every
//      animation frame. Modern browsers fire window.resize inside the iframe each
//      time the iframe element's CSS dimensions change, so the Kinexon renderer
//      receives natural resize events throughout the spring animation.
//
//   B. SDK postMessage bridge (belt-and-suspenders):
//      `map.call("windowResize")` sends `map.windowResize` via the SDK's
//      postMessage bridge. RIoT server versions that expose this through
//      getMethods() will handle it; others silently ignore it.
//      useMotionValueEvent subscribes to mvHeight changes and fires a
//      rAF-throttled call every animation frame so the renderer has every
//      opportunity to snap to the correct viewport, even on servers that
//      batch or debounce their own resize listeners.
//
//   C. Dynamic method detection:
//      getMethods() injects server-side methods at runtime. After the map opens,
//      dynamicResizeRef is populated with the most capable available method:
//        resize() > windowResize() > call("windowResize") > noop
//      All resize calls go through notifyMapResize() which always picks the
//      best available path.

import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  animate as fmAnimate,
} from "framer-motion";
import DisabledTooltip from "../components/disabledToolTip";
import "./rtlsMap.css";

// Helper function to get config from runtime (window.ENV) or build-time (process.env)
const getConfig = (key) => {
  // Try window.ENV first (runtime config from Azure)
  if (
    window.ENV &&
    window.ENV[key] &&
    window.ENV[key] !== `\${REACT_APP_${key}}`
  ) {
    return window.ENV[key];
  }
  // Fallback to build-time environment variable
  return process.env[`REACT_APP_${key}`] || "";
};

// ─── Configuration ────────────────────────────────────────────────────────────
const RTLS_URL = (getConfig("RTLS_URL") || "https://rtlsos.zmodular.com/riot")
  .replace(/[;'"]/g, "")
  .replace(/\/+$/, "");
const RTLS_USER = getConfig("RTLS_USER");
const RTLS_PASS = getConfig("RTLS_PASS");

// Connection state constants
const CONN = {
  IDLE: "idle",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
  ERROR: "error",
};

const CONN_LABELS = {
  [CONN.IDLE]: "Initializing",
  [CONN.CONNECTING]: "Connecting",
  [CONN.CONNECTED]: "Live",
  [CONN.RECONNECTING]: "Reconnecting",
  [CONN.ERROR]: "Connection Error",
};

const CONN_COLORS = {
  [CONN.IDLE]: "#a0aec0",
  [CONN.CONNECTING]: "#d69e2e",
  [CONN.CONNECTED]: "#38a169",
  [CONN.RECONNECTING]: "#d69e2e",
  [CONN.ERROR]: "#cc0000",
};

// ─── Global script loader state ───────────────────────────────────────────────
let globalScriptLoaded = false;
let globalScriptLoading = false;
let globalRiotInitialized = false;
const scriptCallbacks = [];

function loadKinexonScript(callback) {
  if (globalScriptLoaded && window.Riot) {
    callback(true);
    return;
  }

  if (globalScriptLoading) {
    scriptCallbacks.push(callback);
    return;
  }

  const existingScript = document.querySelector('script[src*="jsapi.js"]');
  if (existingScript) {
    globalScriptLoading = true;
    scriptCallbacks.push(callback);

    const checkInterval = setInterval(() => {
      if (window.Riot) {
        clearInterval(checkInterval);
        globalScriptLoaded = true;
        globalScriptLoading = false;
        scriptCallbacks.splice(0).forEach((cb) => cb(true));
      }
    }, 100);

    setTimeout(() => {
      if (globalScriptLoading) {
        clearInterval(checkInterval);
        globalScriptLoading = false;
        console.error("[RtlsMap] Timeout waiting for window.Riot");
        scriptCallbacks.splice(0).forEach((cb) => cb(false));
      }
    }, 10000);
    return;
  }

  globalScriptLoading = true;
  scriptCallbacks.push(callback);

  const scriptUrl = RTLS_URL + "/jsapi.js";
  const script = document.createElement("script");
  script.src = scriptUrl;
  script.async = true;
  script.type = "text/javascript";

  script.onload = () => {
    const checkRiot = () => {
      if (window.Riot) {
        globalScriptLoaded = true;
        globalScriptLoading = false;
        scriptCallbacks.splice(0).forEach((cb) => cb(true));
      } else {
        setTimeout(checkRiot, 100);
      }
    };
    setTimeout(checkRiot, 50);
  };

  script.onerror = (err) => {
    console.error("[RtlsMap] Script load failed:", scriptUrl, err);
    globalScriptLoading = false;
    globalScriptLoaded = false;
    scriptCallbacks.splice(0).forEach((cb) => cb(false));
  };

  document.head.appendChild(script);
}

// ─── Spring config (shared) ───────────────────────────────────────────────────
const EXPAND_SPRING = {
  type: "spring",
  damping: 32,
  stiffness: 280,
  mass: 0.8,
};

// ─── RTLS facility bounds (from Kinexon OS configuration) ─────────────────────
// Source: production Kinexon OS instance — these are the real-world coordinate
// extents of the mapped floor area in the local coordinate system (metres).
const RTLS_BOUNDS = {
  xMin: -224.198,
  xMax: 68.133,
  yMin: 53.83,
  yMax: 101.175,
};

// Derived home-view constants — computed once at module level.
//
//   CENTER_X = (-224.198 + 68.133) / 2  = -78.0325 m
//   CENTER_Y = ( 53.830 + 101.175) / 2  =  77.5025 m
//   WIDTH    =  68.133 − (−224.198)      = 292.331  m
//   HEIGHT   = 101.175 −   53.830        =  47.345  m
const MAP_HOME_CENTER_X = (RTLS_BOUNDS.xMin + RTLS_BOUNDS.xMax) / 2; // -78.0325
const MAP_HOME_CENTER_Y = (RTLS_BOUNDS.yMin + RTLS_BOUNDS.yMax) / 2; //  77.5025
const MAP_HOME_BOUNDS_W = RTLS_BOUNDS.xMax - RTLS_BOUNDS.xMin; // 292.331 m
const MAP_HOME_BOUNDS_H = RTLS_BOUNDS.yMax - RTLS_BOUNDS.yMin; //  47.345 m

// Fraction of the viewport used by the bounds (the rest becomes padding margin).
const MAP_HOME_PADDING = 0.88;

// Zoom value used when getViewport()/getZoom() are unavailable on the server.
// 0.1 is the conservative "show the whole map" fallback from the original code.
const MAP_HOME_ZOOM_FALLBACK = 0.1;

// ─── Component ────────────────────────────────────────────────────────────────
//
// filterQuery prop — a RIoT search query string passed in by the parent page.
//
// Query format (verify against your RIoT instance's /help page):
//   Label-only  (default view):  "labelId:1011"      (all Modules)
//   Label-only  (default view):  "labelId:1006"      (all Employees)
//   Object-only (item selected): 'name:"WO-001234"'
//   Combined    (most specific): 'labelId:1011 name:"WO-001234"'
//
// Passing null / undefined → no filter call is made (map shows everything).
// Passing an empty string "" → clears all filters (shows every object).
//
const RtlsMap = ({ isHomePage = false, filterQuery = null }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [heatmapActive, setHeatmapActive] = useState(false);
  const [spaghettiActive, setSpaghettiActive] = useState(false);
  const [replayActive, setReplayActive] = useState(false);
  const [connStatus, setConnStatus] = useState(CONN.IDLE);
  const [livePositions, setLivePositions] = useState([]);

  // ── Existing RTLS refs ──────────────────────────────────────────────────────
  const mapContainerRef = useRef(null);
  const riotMapRef = useRef(null);
  const riotRef = useRef(null);
  const apiRef = useRef(null);
  const posSubRef = useRef(null);
  const loggedInRef = useRef(false);
  const mountedRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const unsubscribersRef = useRef({
    connectionStatus: null,
    error: null,
    identityReset: null,
  });

  // ── Filter query ref ────────────────────────────────────────────────────────
  // Stores the latest filterQuery prop value so the renderMap "load" callback
  // (a stable useCallback closure created once) can always read the current
  // filter without needing filterQuery in its dependency array — which would
  // tear down and recreate the map on every selection change.
  const filterQueryRef = useRef(filterQuery);
  useEffect(() => {
    filterQueryRef.current = filterQuery;
    console.log(
      `[RtlsMap FILTER] filterQueryRef updated → "${filterQuery ?? "(null)"}"`,
    );
  }, [filterQuery]);

  // ── applyFilter ─────────────────────────────────────────────────────────────
  // Calls map.filterObjects(query) on the live map instance.
  // Safe to call at any time — guards against missing map or null query.
  const applyFilter = useCallback((query) => {
    if (query === null || query === undefined) {
      console.log(
        "[RtlsMap FILTER] applyFilter: query is null/undefined — skipping",
      );
      return;
    }
    if (!riotMapRef.current) {
      console.log(
        `[RtlsMap FILTER] applyFilter: map not ready yet, query will be applied on load → "${query}"`,
      );
      return;
    }
    try {
      console.log(
        `[RtlsMap FILTER] applyFilter: calling filterObjects("${query}")`,
      );
      // riotMapRef.current.filterObjects(query);
      console.log(
        `[RtlsMap FILTER] applyFilter: filterObjects succeeded → "${query}"`,
      );
    } catch (err) {
      console.warn("[RtlsMap FILTER] applyFilter: filterObjects threw:", err);
    }
  }, []);

  // ── React to filterQuery prop changes ────────────────────────────────────────
  // Whenever the parent page changes the filter (new selection, cleared search,
  // default label view, etc.) push it straight to the embedded map.
  // If the map isn't loaded yet, filterQueryRef holds the value and renderMap's
  // "load" handler picks it up.
  useEffect(() => {
    console.log(
      `[RtlsMap FILTER] filterQuery prop changed → "${filterQuery ?? "(null)"}" | map ready: ${!!riotMapRef.current}`,
    );
    applyFilter(filterQuery);
  }, [filterQuery, applyFilter]);

  // ── Dev console: manual filter override ────────────────────────────────────
  // Exposes window.setRtlsFilter(query) in the browser console so developers
  // can test filter queries without going through the full UI flow.
  // Works alongside the existing auto-filter pipeline (ref data + search results):
  //   window.setRtlsFilter('labelId:1011')          → show all Modules
  //   window.setRtlsFilter('name:"WO-001234"')      → highlight a single WO
  //   window.setRtlsFilter('')                       → clear all filters
  //   window.setRtlsFilter(null)                     → no-op (same as no prop)
  useEffect(() => {
    window.setRtlsFilter = (query) => {
      console.log(
        `[RtlsMap FILTER] window.setRtlsFilter("${query ?? "(null)"}")`,
      );
      applyFilter(query);
    };
    return () => {
      delete window.setRtlsFilter;
    };
  }, [applyFilter]);

  // ── Resize notification refs ─────────────────────────────────────────────────
  //
  // dynamicResizeRef — populated by detectResizeMethods() once the map opens.
  // Holds the best available resize notification function, resolved in order:
  //   1. map.resize()       — if getMethods() returned "resize"
  //   2. map.windowResize() — if getMethods() returned "windowResize"
  //   3. map.call("windowResize") — always available as fallback via postMessage
  //
  // This ref is used by notifyMapResize() so the best path is always taken
  // without a conditional chain on every animation frame.
  const dynamicResizeRef = useRef(null);

  // rAF handle for the per-frame throttle. Ensures at most one resize
  // notification is in flight per animation frame, even if motion value
  // events fire multiple times per frame.
  const resizeRafRef = useRef(null);

  // ── Framer Motion refs & values ─────────────────────────────────────────────
  const spacerRef = useRef(null);
  // Mirror of isExpanded state, readable inside rAF callbacks without
  // stale-closure issues.
  const isExpandedRef = useRef(false);

  // Motion values drive the card's CSS directly (no React re-render on change)
  const mvLeft = useMotionValue(0);
  const mvTop = useMotionValue(0);
  const mvWidth = useMotionValue(300);
  const mvHeight = useMotionValue(500);
  const mvRadius = useMotionValue(16);
  const mvZ = useMotionValue(10);

  // \u2500\u2500 Page-load entrance animation \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
  // Driven by Framer Motion motion values so they stay in the same system as
  // the geometry values and never conflict with Framer Motion's own transform
  // management.  A CSS animation cannot be used on this element because Framer
  // Motion writes its own transform, which instantly overrides a CSS animation's
  // translateY starting state \u2014 causing the visible "snap".
  //
  // Mirrors pageItemEnter: opacity 0\u21921, translateY 16px\u21920, 0.4s ease, 0.15s delay.
  // Placing the map between the page header (0.05s/0.10s) and the first metric
  // card (0.20s) in the stagger sequence.
  const mvOpacity = useMotionValue(0);
  const mvEnterY = useMotionValue(16);

  useEffect(() => {
    const t = setTimeout(() => {
      fmAnimate(mvOpacity, 1, { duration: 0.4, ease: "easeOut" });
      fmAnimate(mvEnterY, 0, { duration: 0.4, ease: "easeOut" });
    }, 150);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resize notification helpers ─────────────────────────────────────────────

  // notifyMapResize — calls the best available SDK method to tell the
  // Kinexon renderer that the viewport has changed.  Always safe to call;
  // all paths are wrapped in try/catch.
  const notifyMapResize = useCallback(() => {
    if (!riotMapRef.current) return;
    try {
      if (dynamicResizeRef.current) {
        // Use the pre-resolved best method (set by detectResizeMethods)
        dynamicResizeRef.current();
      } else {
        // Fallback before getMethods has resolved: raw postMessage bridge.
        // Sends "map.windowResize" to the iframe; silently ignored if
        // the server doesn't implement it.
        riotMapRef.current.call("windowResize");
      }
    } catch (_err) {
      // Never throw — this is called on every animation frame
    }
  }, []);

  // scheduleMapResize — rAF-throttled wrapper around notifyMapResize.
  // The guard collapses multiple motion-value events within the same
  // frame into a single SDK call, keeping notification cost minimal.
  const scheduleMapResize = useCallback(() => {
    if (resizeRafRef.current !== null) return;
    resizeRafRef.current = requestAnimationFrame(() => {
      resizeRafRef.current = null;
      notifyMapResize();
    });
  }, [notifyMapResize]);

  // detectResizeMethods — called once after the map's "load" event fires.
  // By that point getMethods() has had time to inject any server-side
  // dynamic methods into the map instance.
  // Resolution order (best → safest fallback):
  //   resize()       — dedicated resize, most explicit
  //   windowResize() — fires the internal window resize path
  //   call("windowResize") — raw postMessage; always available
  const detectResizeMethods = useCallback((mapInstance) => {
    // 200 ms headroom so getMethods() resolution is complete before we probe
    setTimeout(() => {
      if (!mountedRef.current || !mapInstance) return;

      if (typeof mapInstance.resize === "function") {
        dynamicResizeRef.current = () => mapInstance.resize();
        //console.log("[RtlsMap] resize(): using getMethods-injected resize()");
      } else if (typeof mapInstance.windowResize === "function") {
        dynamicResizeRef.current = () => mapInstance.windowResize();
        //console.log("[RtlsMap] resize(): using getMethods-injected windowResize()");
      } else {
        // Server did not expose a dedicated resize method; use the bridge.
        dynamicResizeRef.current = () => mapInstance.call("windowResize");
        //console.log("[RtlsMap] resize(): using call('windowResize') postMessage bridge");
      }
    }, 200);
  }, []);

  // ── Sync card position to spacer (instant) ──────────────────────────────────
  const syncToSpacer = useCallback(() => {
    if (!spacerRef.current || isExpandedRef.current) return;
    const r = spacerRef.current.getBoundingClientRect();
    mvLeft.set(r.left);
    mvTop.set(r.top);
    mvWidth.set(r.width);
    mvHeight.set(r.height);
  }, [mvLeft, mvTop, mvWidth, mvHeight]);

  // Initial measurement before first paint
  useLayoutEffect(() => {
    syncToSpacer();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track spacer bounds continuously (sidebar transitions, window resize, scroll)
  //
  // WHY a scroll listener is required
  // ──────────────────────────────────
  // The portal card is `position: fixed`, so its left/top must be given in
  // viewport-relative coordinates.  getBoundingClientRect() already returns
  // viewport-relative values, which is correct — BUT those values change
  // whenever ANY ancestor scrollable container scrolls (not just window).
  //
  // At desktop widths, .home-page has `overflow: hidden` so nothing scrolls.
  // Below 1024 px, .home-page switches to `overflow: auto`.  When the user
  // scrolls inside that element the spacer's getBoundingClientRect().top changes
  // but neither ResizeObserver (size-only) nor window "resize" fires, so the
  // fixed card drifts away from the spacer and appears "sticky in the center."
  //
  // Solution: listen in capture phase on window so ALL scroll events bubble up
  // from every scrollable ancestor, and re-sync to the spacer's current
  // viewport position.  The collapsed guard in syncToSpacer means this is a
  // no-op while the card is in expanded (fullscreen) mode.
  useEffect(() => {
    const ro = new ResizeObserver(syncToSpacer);
    if (spacerRef.current) ro.observe(spacerRef.current);
    window.addEventListener("resize", syncToSpacer);
    // Capture phase so the listener fires for scroll events on ANY element,
    // not just window — covers .home-page overflow:auto on narrow viewports.
    window.addEventListener("scroll", syncToSpacer, {
      capture: true,
      passive: true,
    });
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", syncToSpacer);
      window.removeEventListener("scroll", syncToSpacer, { capture: true });
    };
  }, [syncToSpacer]);

  // ── Per-frame resize notifications during animation ──────────────────────────
  //
  // useMotionValueEvent fires synchronously inside Framer Motion's animation
  // loop on every frame where the value changes.  We subscribe to both
  // mvHeight and mvWidth so sidebar-only resizes (width changes, height stays)
  // are also caught.  The rAF throttle collapses both events, which fire in
  // the same frame, into a single notifyMapResize() call.
  useMotionValueEvent(mvHeight, "change", scheduleMapResize);
  useMotionValueEvent(mvWidth, "change", scheduleMapResize);

  // Cancel any pending rAF on unmount to avoid calling into a dead ref
  useEffect(() => {
    return () => {
      if (resizeRafRef.current !== null) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
    };
  }, []);

  // Lock body scroll when expanded
  useEffect(() => {
    document.body.style.overflow = isExpanded ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isExpanded]);

  // ESC key closes expanded view
  useEffect(() => {
    if (!isExpanded) return;
    const onKey = (e) => {
      if (e.key === "Escape") toggleExpand();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Complete teardown helper ────────────────────────────────────────────────
  const teardown = useCallback(() => {
    if (posSubRef.current) {
      try {
        posSubRef.current.unsubscribe();
      } catch (_) {}
      posSubRef.current = null;
    }

    if (riotMapRef.current && mapContainerRef.current) {
      try {
        mapContainerRef.current.innerHTML = "";
      } catch (_) {}
      riotMapRef.current = null;
    }

    const unsubs = unsubscribersRef.current;
    if (apiRef.current) {
      try {
        if (typeof unsubs.connectionStatus === "function")
          unsubs.connectionStatus();
        if (typeof unsubs.error === "function") unsubs.error();
        if (typeof unsubs.identityReset === "function") unsubs.identityReset();
      } catch (_) {}
    }

    unsubscribersRef.current = {
      connectionStatus: null,
      error: null,
      identityReset: null,
    };
    apiRef.current = null;
    riotRef.current = null;
    loggedInRef.current = false;
    dynamicResizeRef.current = null; // reset on remount so detection re-runs
    setLivePositions([]);
  }, []);

  // ── Render map ──────────────────────────────────────────────────────────────
  const renderMap = useCallback(
    (Riot) => {
      if (!mountedRef.current) return;

      const container = mapContainerRef.current;
      if (!container) {
        console.warn("[RtlsMap] Map container not available");
        return;
      }

      Riot.Api.locationApi
        .getLocationList()
        .then((locations) => {
          if (!mountedRef.current || !locations || locations.length === 0) {
            console.warn("[RtlsMap] No locations available");
            return;
          }

          const location = locations[0];
          const config = location.configs && location.configs[0];
          if (!config) {
            console.warn("[RtlsMap] No config found for location");
            return;
          }

          if (riotMapRef.current) {
            container.innerHTML = "";
            riotMapRef.current = null;
          }

          const map = new Riot.Map(container);
          riotMapRef.current = map;

          map.on("load", (success) => {
            if (!success) {
              console.warn("[RtlsMap] Map failed to load");
              return;
            }
            try {
              map.switchMode(Riot.Map.MODE_2D);
              map.toggleObjects(true, true);
            } catch (err) {
              console.warn("[RtlsMap] Error configuring map:", err);
            }

            // Detect the best available resize method now that the map is open
            // and getMethods() has run to inject any server-side dynamic methods.
            detectResizeMethods(map);
          });

          // ── AFTER ──────────────────────────────────────────────────────────────────
          map.on("load", (success) => {
            if (!success) {
              console.warn("[RtlsMap] Map failed to load");
              return;
            }
            try {
              map.switchMode(Riot.Map.MODE_2D);
              map.toggleObjects(true, true);
            } catch (err) {
              console.warn("[RtlsMap] Error configuring map:", err);
            }

            // Detect the best available resize method now that the map is open
            // and getMethods() has run to inject any server-side dynamic methods.
            detectResizeMethods(map);

            // ── Apply any pending filter that arrived before the map was ready ──────
            // filterQueryRef always holds the latest prop value (updated in a
            // separate useEffect above). If the parent page set a filter before
            // this load callback fired, we apply it now that riotMapRef is live.
            const pendingFilter = filterQueryRef.current;
            if (pendingFilter !== null && pendingFilter !== undefined) {
              console.log(
                `[RtlsMap FILTER] load: applying pending filter → "${pendingFilter}"`,
              );
              try {
                // map.filterObjects(pendingFilter);
              } catch (err) {
                console.warn(
                  "[RtlsMap FILTER] load: filterObjects threw:",
                  err,
                );
              }
            }
          });

          map.on("no-location", () => {
            map.setLocation(8, 1);
          });

          map.render();
          map.setLocation(8, 1);
        })
        .catch((err) => {
          console.error("[RtlsMap] Failed to get locations:", err);
        });
    },
    [detectResizeMethods],
  );

  // ── Subscribe to position updates ───────────────────────────────────────────
  const subscribePositions = useCallback((Riot) => {
    if (!mountedRef.current) return;

    if (posSubRef.current) {
      try {
        posSubRef.current.unsubscribe();
      } catch (_) {}
    }

    try {
      posSubRef.current = Riot.Api.positionApi.rsig.positionUpdated
        .register()
        .subscribe((pos) => {
          if (!mountedRef.current) return;
          setLivePositions((prev) => {
            const idx = prev.findIndex((p) => p.objectId === pos.objectId);
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = pos;
              return next;
            }
            return [...prev, pos];
          });
        });
    } catch (err) {
      console.error("[RtlsMap] Failed to subscribe to positions:", err);
    }
  }, []);

  // ── Authenticate ────────────────────────────────────────────────────────────
  const authenticate = useCallback(
    (Riot) => {
      if (!mountedRef.current || loggedInRef.current) return;

      Riot.Api.userApi
        .isLoggedIn()
        .then((isLoggedIn) => {
          if (!mountedRef.current) return;

          if (isLoggedIn) {
            loggedInRef.current = true;
            setConnStatus(CONN.CONNECTED);
            renderMap(Riot);
            subscribePositions(Riot);
          } else {
            Riot.Api.userApi
              .login(RTLS_USER, RTLS_PASS)
              .then(([loginSuccess]) => {
                if (!mountedRef.current) return;
                if (!loginSuccess) {
                  console.error("[RtlsMap] Login failed");
                  setConnStatus(CONN.ERROR);
                  return;
                }

                Riot.Api.userApi
                  .getAvailableTenantsAndRoles()
                  .then((roles) => {
                    if (!mountedRef.current || !roles || roles.length === 0) {
                      console.error("[RtlsMap] No roles available");
                      setConnStatus(CONN.ERROR);
                      return;
                    }

                    const roleId = roles[0].roleIdName[0][0];
                    Riot.Api.userApi.selectRole(roleId).then((roleSuccess) => {
                      if (!mountedRef.current) return;
                      if (!roleSuccess) {
                        console.error("[RtlsMap] Role selection failed");
                        setConnStatus(CONN.ERROR);
                        return;
                      }
                      loggedInRef.current = true;
                      setConnStatus(CONN.CONNECTED);
                      renderMap(Riot);
                      subscribePositions(Riot);
                    });
                  })
                  .catch((err) => {
                    if (!mountedRef.current) return;
                    console.error("[RtlsMap] Failed to get roles:", err);
                    setConnStatus(CONN.ERROR);
                  });
              })
              .catch((err) => {
                if (!mountedRef.current) return;
                console.error("[RtlsMap] Login error:", err);
                setConnStatus(CONN.ERROR);
              });
          }
        })
        .catch((err) => {
          if (!mountedRef.current) return;
          console.error("[RtlsMap] Auth check error:", err);
          setConnStatus(CONN.ERROR);
        });
    },
    [renderMap, subscribePositions],
  );

  // ── Initialize Kinexon ──────────────────────────────────────────────────────
  const initializeKinexon = useCallback(() => {
    if (hasInitializedRef.current || !mountedRef.current) return;

    hasInitializedRef.current = true;
    setConnStatus(CONN.CONNECTING);

    loadKinexonScript((success) => {
      if (!mountedRef.current) return;
      if (!success) {
        console.error("[RtlsMap] Script load failed");
        setConnStatus(CONN.ERROR);
        return;
      }

      const Riot = window.Riot;
      if (!Riot) {
        console.error("[RtlsMap] window.Riot is undefined");
        setConnStatus(CONN.ERROR);
        return;
      }

      riotRef.current = Riot;

      try {
        if (!globalRiotInitialized) {
          Riot.init(RTLS_URL);
          globalRiotInitialized = true;
        }

        if (!Riot.Api) {
          console.error("[RtlsMap] Riot.Api is undefined after Riot.init()");
          setConnStatus(CONN.ERROR);
          return;
        }

        const Api = Riot.Api;
        apiRef.current = Api;

        if (Api.rmi && Api.rmi.isRunning && Api.rmi.isRunning()) {
          setConnStatus(CONN.CONNECTED);
          authenticate(Riot);
          return;
        }

        const connStatusUnsub = Api.on("connectionStatus", (status) => {
          if (!mountedRef.current) return;
          if (status === 1) {
            setConnStatus(CONN.CONNECTED);
            authenticate(Riot);
          } else if (status === 2) {
            loggedInRef.current = false;
            setConnStatus(CONN.RECONNECTING);
          } else {
            setConnStatus(CONN.IDLE);
          }
        });

        if (typeof connStatusUnsub === "function") {
          unsubscribersRef.current.connectionStatus = connStatusUnsub;
        }

        const errorUnsub = Api.on("error", (err) => {
          if (!mountedRef.current) return;
          console.warn("[RtlsMap] WebSocket error:", err);
          setConnStatus(CONN.RECONNECTING);
        });

        if (typeof errorUnsub === "function") {
          unsubscribersRef.current.error = errorUnsub;
        }

        const identityResetUnsub = Api.on("identityReset", () => {
          if (!mountedRef.current) return;
          loggedInRef.current = false;
          setConnStatus(CONN.RECONNECTING);
          if (riotRef.current) authenticate(riotRef.current);
        });

        if (typeof identityResetUnsub === "function") {
          unsubscribersRef.current.identityReset = identityResetUnsub;
        }

        Api.init();
      } catch (err) {
        console.error("[RtlsMap] Initialization error:", err);
        setConnStatus(CONN.ERROR);
      }
    });
  }, [authenticate]);

  // ── Mount / unmount ─────────────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    initializeKinexon();
    return () => {
      mountedRef.current = false;
      teardown();
      setTimeout(() => {
        if (!mountedRef.current) hasInitializedRef.current = false;
      }, 100);
    };
  }, [initializeKinexon, teardown]);

  // ── Button handlers ─────────────────────────────────────────────────────────

  // handleMapHome — recenter to the midpoint of the RTLS facility bounds and
  // zoom to fit the entire mapped area in the visible viewport.
  //
  // Zoom-to-fit formula
  // ───────────────────
  // The RIoT API provides two relevant queries:
  //   getViewport() → current visible area in world-space metres (V_w × V_h)
  //   getZoom()     → current zoom level Z (a real number)
  //
  // Both are fetched in parallel before any mutation so the calculation is
  // based on the pre-move state (panning does not change zoom or viewport
  // dimensions, so this is safe).
  //
  // Because zoom is proportional to scale (higher = more zoomed in), the
  // target zoom that makes BOUNDS_W × BOUNDS_H fill the viewport is:
  //
  //   targetZoom = Z × min(V_w / BOUNDS_W, V_h / BOUNDS_H) × PADDING
  //
  // min() picks the tighter axis so the whole building fits (fit, not fill).
  // PADDING leaves a comfortable margin around the edges.
  //
  // This formula is self-consistent: it never needs to know the absolute
  // pixel-per-metre conversion — it only ratios against the current viewport.
  //
  // Fallback: if getViewport() / getZoom() are not available on this server
  // version (they are requested via postMessage and may not be implemented),
  // MAP_HOME_ZOOM_FALLBACK is used instead.
  const handleMapHome = useCallback(async () => {
    if (!riotMapRef.current) return;

    const map = riotMapRef.current;
    try {
      // ── Step 1: query current viewport & zoom in parallel (pre-move) ─────────
      // Panning does not change zoom or viewport dimensions, so querying before
      // setPosition() gives the correct basis for the scale calculation.
      let targetZoom = MAP_HOME_ZOOM_FALLBACK;
      try {
        const [viewport, currentZoom] = await Promise.all([
          map.getViewport(),
          map.getZoom(),
        ]);

        if (
          viewport &&
          typeof currentZoom === "number" &&
          isFinite(currentZoom) &&
          currentZoom > 0
        ) {
          // getViewport() may return { width, height }, { w, h }, or
          // { minX, maxX, minY, maxY } depending on the server version.
          const viewW =
            viewport.width ??
            viewport.w ??
            (viewport.maxX != null ? viewport.maxX - viewport.minX : null);
          const viewH =
            viewport.height ??
            viewport.h ??
            (viewport.maxY != null ? viewport.maxY - viewport.minY : null);

          if (viewW > 0 && viewH > 0) {
            // Scale so the tighter axis fits; PADDING adds breathing room.
            const scale = Math.min(
              viewW / MAP_HOME_BOUNDS_W,
              viewH / MAP_HOME_BOUNDS_H,
            );
            targetZoom = currentZoom * scale * MAP_HOME_PADDING;
          }
        }
      } catch (_viewportErr) {
        // getViewport() / getZoom() not implemented on this server version;
        // fall through to MAP_HOME_ZOOM_FALLBACK.
      }

      // ── Step 2: center on the facility midpoint (local coords, metres) ───────
      map.setPosition(MAP_HOME_CENTER_X, MAP_HOME_CENTER_Y, 0);

      // ── Step 3: apply the computed (or fallback) zoom level ──────────────────
      map.setZoom(targetZoom);
    } catch (err) {
      console.warn("[RtlsMap] Error recentering map:", err);
    }
  }, []); // no reactive deps — all values are module-level constants or refs

  // ── Expand / collapse ───────────────────────────────────────────────────────
  const toggleExpand = useCallback(() => {
    const nextExpanded = !isExpandedRef.current;
    isExpandedRef.current = nextExpanded;
    setIsExpanded(nextExpanded);

    if (nextExpanded) {
      // Snap card to current spacer position, then spring outward.
      // Snapping first means the animation always originates from the card's
      // true visual position, regardless of sidebar state.
      if (spacerRef.current) {
        const r = spacerRef.current.getBoundingClientRect();
        mvLeft.set(r.left);
        mvTop.set(r.top);
        mvWidth.set(r.width);
        mvHeight.set(r.height);
      }
      mvZ.set(500);

      fmAnimate(mvLeft, 0, EXPAND_SPRING);
      fmAnimate(mvTop, 0, EXPAND_SPRING);
      fmAnimate(mvWidth, window.innerWidth, EXPAND_SPRING);
      fmAnimate(mvRadius, 0, EXPAND_SPRING);

      // Chain a guaranteed "animation settled" resize on the height spring.
      // useMotionValueEvent fires throughout the spring; this final call
      // is the authoritative notification at exactly the resting position.
      fmAnimate(mvHeight, window.innerHeight, EXPAND_SPRING).then(() => {
        if (mountedRef.current) notifyMapResize();
      });
    } else {
      // Spring back to wherever the spacer currently sits
      const r = spacerRef.current?.getBoundingClientRect();
      if (r) {
        fmAnimate(mvLeft, r.left, EXPAND_SPRING);
        fmAnimate(mvTop, r.top, EXPAND_SPRING);
        fmAnimate(mvWidth, r.width, EXPAND_SPRING);
        fmAnimate(mvRadius, 16, EXPAND_SPRING);

        fmAnimate(mvHeight, r.height, EXPAND_SPRING).then(() => {
          // Drop z-index after the spring has fully settled
          mvZ.set(10);
          // Final resize at restored dimensions
          if (mountedRef.current) notifyMapResize();
        });
      }
    }
  }, [mvLeft, mvTop, mvWidth, mvHeight, mvRadius, mvZ, notifyMapResize]);

  const toggleHeatmap = useCallback(() => {
    setHeatmapActive((prev) => {
      const next = !prev;
      if (riotMapRef.current) {
        try {
          // Future: riotMapRef.current.call("toggleHeatmap", next);
        } catch (err) {
          console.warn("[RtlsMap] Heatmap toggle error:", err);
        }
      }
      return next;
    });
  }, []);

  const toggleSpaghetti = useCallback(() => {
    setSpaghettiActive((prev) => {
      const next = !prev;
      if (riotMapRef.current) {
        try {
          // Future: riotMapRef.current.call("toggleTrails", next);
        } catch (err) {
          console.warn("[RtlsMap] Spaghetti toggle error:", err);
        }
      }
      return next;
    });
  }, []);

  const toggleReplay = useCallback(() => {
    setReplayActive((prev) => !prev);
  }, []);

  const showExtraButtons = isHomePage || isExpanded;

  // ── Card JSX ─────────────────────────────────────────────────────────────────
  const cardContent = (
    <>
      {/* ── Section header ───────────────────────────────────── */}
      <div className="rtls-section-header">
        <div className="rtls-header-left">
          <h2>Live Facility Map</h2>
          <div className="rtls-conn-indicator">
            <div
              className={`rtls-conn-dot${
                connStatus === CONN.RECONNECTING
                  ? " rtls-conn-dot--reconnecting"
                  : ""
              }`}
              style={{ background: CONN_COLORS[connStatus] }}
            />
            <span className="rtls-conn-label">{CONN_LABELS[connStatus]}</span>
            {connStatus === CONN.CONNECTED && livePositions.length > 0 && (
              <span className="rtls-conn-count">
                {livePositions.length} object
                {livePositions.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>

        <div className="rtls-map-controls">
          {/* Recenter */}
          <button
            className="rtls-map-control-btn"
            onClick={handleMapHome}
            title="Recenter map"
            disabled={connStatus !== CONN.CONNECTED}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </button>

          {/* Expand / collapse */}
          <button
            className={`rtls-map-control-btn${isExpanded ? " active" : ""}`}
            onClick={toggleExpand}
            title={isExpanded ? "Exit fullscreen" : "Expand fullscreen"}
          >
            {isExpanded ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
              </svg>
            )}
          </button>

          {showExtraButtons && (
            <>
              <DisabledTooltip>
                <button
                  className={`rtls-map-control-btn${heatmapActive ? " active" : ""}`}
                  onClick={toggleHeatmap}
                  title="Toggle heatmap"
                  disabled={connStatus !== CONN.CONNECTED}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="3" />
                    <circle cx="12" cy="12" r="7" opacity="0.5" />
                    <circle cx="12" cy="12" r="10" opacity="0.25" />
                  </svg>
                </button>
              </DisabledTooltip>

              <DisabledTooltip>
                <button
                  className={`rtls-map-control-btn${spaghettiActive ? " active" : ""}`}
                  onClick={toggleSpaghetti}
                  title="Toggle movement paths"
                  disabled={connStatus !== CONN.CONNECTED}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M3 12c0-4 2-8 6-8s6 4 6 8-2 8-6 8" />
                    <path d="M15 4c4 0 6 4 6 8s-2 8-6 8" />
                  </svg>
                </button>
              </DisabledTooltip>

              <DisabledTooltip>
                <button
                  className={`rtls-map-control-btn${replayActive ? " active" : ""}`}
                  onClick={toggleReplay}
                  title="Toggle replay mode"
                  disabled={connStatus !== CONN.CONNECTED}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                </button>
              </DisabledTooltip>
            </>
          )}
        </div>
      </div>

      {/* ── Map area ─────────────────────────────────────────── */}
      {connStatus === CONN.ERROR ? (
        <div className="rtls-map-placeholder rtls-map-placeholder--error">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <circle cx="12" cy="16" r="0.5" fill="currentColor" />
          </svg>
          <p>Unable to connect to RTLS</p>
          <span className="rtls-placeholder-note">
            Check that the Kinexon server is reachable at{" "}
            <code>{RTLS_URL}</code> and that credentials are correct. Verify the
            jsapi.js script is accessible and CORS is enabled.
          </span>
        </div>
      ) : (
        <div
          ref={mapContainerRef}
          className={`rtls-map-iframe-wrap${
            connStatus !== CONN.CONNECTED
              ? " rtls-map-iframe-wrap--loading"
              : ""
          }`}
        >
          {connStatus !== CONN.CONNECTED && (
            <div className="rtls-map-loading-overlay">
              <div className="rtls-spinner" />
              <span>{CONN_LABELS[connStatus]}…</span>
            </div>
          )}
        </div>
      )}
    </>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/*
        Spacer — always in the grid at this component's slot.
        Invisible but occupies the exact space the card would.
        ResizeObserver watches this so the portal card tracks it
        continuously, including during sidebar width transitions.
      */}
      <div ref={spacerRef} className="rtls-map-spacer" aria-hidden="true" />

      {/*
        Portal card — always `position: fixed`, always in the DOM.
        Framer Motion animates left/top/width/height/borderRadius via
        motion values, so the Riot.Map iframe is never unmounted or
        re-created during expand/collapse.

        useMotionValueEvent on mvHeight + mvWidth fires scheduleMapResize
        on every animation frame, notifying the Kinexon renderer via the
        SDK's postMessage bridge throughout the spring animation.
      */}
      {createPortal(
        <motion.div
          className={`rtls-map-container${isExpanded ? " rtls-map-container--expanded" : ""}`}
          style={{
            position: "fixed",
            left: mvLeft,
            top: mvTop,
            width: mvWidth,
            height: mvHeight,
            borderRadius: mvRadius,
            zIndex: mvZ,
            opacity: mvOpacity,
            y: mvEnterY,
            boxSizing: "border-box",
            // Framer Motion owns all geometry — no CSS transition interference
            transition: "none",
          }}
        >
          {cardContent}
        </motion.div>,
        document.body,
      )}
    </>
  );
};

export default RtlsMap;
